// The plan stories' LEG READ (plan 101 slice 12) — how one story's stored truth becomes the
// per-leg facts `GET /v1/plans/:planId` serves (`edges` with `released`, `deploy`, `mergeHeld`)
// and the plan's `goalState`, which `GET /v1/plans` serves too.
//
// A story declares what the SERVER STORES — the edges a writer stated (`plan_group_edges`) and
// the newest deploy verdict per environment (`deploy_verdicts`) — and never what the read
// DERIVES. The derivation is the domain's own wherever the domain has one: `planEdgesOf` turns
// a bare upstream id into the `merged` / `start` edge it has always meant, `planLegDeploys`
// folds the chips, `mergeHeldOf` projects the merge holds and `planGoalState` labels the plan.
// The one judgement made here is `released`, which the server makes in SQL
// (`planEdgeReleasedSql`): `merged` = the upstream is satisfied (landed or settled no work);
// `deployed` = satisfied AND its merge's newest verdict in the edge's environment is a success.
// It reads the upstream's chip, which `planLegDeploys` built from the same verdict.

import {
  mergeHeldOf,
  type PlanEdge,
  PlanEdgeRelease,
  type PlanGoalState,
  type PlanProjectionEdge,
  type PlanProjectionGroup,
  type PlanStatus,
  planEdgesOf,
  planGoalState,
  planLegDeploys,
} from '@developerz/domain';

/**
 * One story slice: the wire group minus the three fields the read derives, plus the stored
 * facts it derives them from. `edgeSpecs` absent means `dependsOn` in the bare-id shorthand.
 */
export type StoryGroup = Omit<PlanProjectionGroup, 'edges' | 'deploy' | 'mergeHeld'> & {
  readonly edgeSpecs?: readonly PlanEdge[];
  readonly verdicts?: readonly { environment: string; conclusion: string | null }[];
};

const satisfied = (g: Pick<PlanProjectionGroup, 'disposition'>): boolean =>
  g.disposition === 'landed' || g.disposition === 'no_work';

/** The stored edges of one story slice, sorted by upstream as the projection serves them. */
function storedEdges(g: StoryGroup): PlanEdge[] {
  return planEdgesOf(g.edgeSpecs ?? g.dependsOn).sort((a, b) =>
    a.upstream.localeCompare(b.upstream),
  );
}

/** Every story slice as the plan read serves it. */
export function legReads(groups: readonly StoryGroup[]): PlanProjectionGroup[] {
  const edgesOf = new Map(groups.map((g) => [g.groupId, storedEdges(g)] as const));
  const awaited = (id: string): string[] =>
    [...edgesOf.values()]
      .flat()
      .filter((e) => e.upstream === id && e.environment !== null)
      .map((e) => e.environment as string);
  const deployOf = new Map(
    groups.map((g) => [
      g.groupId,
      planLegDeploys({
        merged: g.status === 'merged',
        sha: g.pullRequest?.mergeCommitSha ?? null,
        verdicts: g.verdicts ?? [],
        awaitedEnvironments: awaited(g.groupId),
      }),
    ]),
  );
  const byId = new Map(groups.map((g) => [g.groupId, g] as const));
  const released = (e: PlanEdge): boolean => {
    const upstream = byId.get(e.upstream);
    if (upstream === undefined || !satisfied(upstream)) return false;
    if (e.release === PlanEdgeRelease.Merged) return true;
    return (deployOf.get(e.upstream) ?? []).some(
      (d) => d.environment === e.environment && d.state === 'deployed',
    );
  };
  return groups.map(({ edgeSpecs: _specs, verdicts: _verdicts, ...g }) => {
    const edges: PlanProjectionEdge[] = (edgesOf.get(g.groupId) ?? []).map((e) => ({
      ...e,
      released: released(e),
    }));
    return {
      ...g,
      dependsOn: edges.map((e) => e.upstream),
      edges,
      deploy: deployOf.get(g.groupId) ?? [],
      mergeHeld: mergeHeldOf(edges),
    };
  });
}

/** `planGoalState` over the served legs — the call `getPlanProjection` and the listing make. */
export function storyGoalState(
  status: PlanStatus,
  groups: readonly PlanProjectionGroup[],
): PlanGoalState {
  return planGoalState({
    status,
    legs: groups.map((g) => ({
      groupId: g.groupId,
      disposition: g.disposition,
      pullRequestOpen: g.pullRequest?.state === 'open',
      // No story is a `live_check` leaf: slice 09 has not added the outcome kind.
      liveCheck: false,
      edges: g.edges,
    })),
  });
}
